/**
 * The game description (D156) — what a shell shows AROUND a game, fixed for
 * the life of a session: the masthead, the title screen, the layout's one
 * aspect ratio, whether there is sound, whether there is a clock. The
 * presentation block (D132) resolved, with its asset paths already joined
 * to the game's asset base, plus the two facts the session controls need
 * before any turn has run.
 *
 * @packageDocumentation
 */

import { playModeSchema, surfaceSchema } from '@orpheus/content-schema';
import { z } from 'zod';

import { ambienceTargetSchema } from './session-view.js';

const label = z.string().min(1);

/** The title screen, resolved: labels, the hero plate, the theme bed. */
export const titleScreenViewSchema = z
	.strictObject({
		beginLabel: label,
		hero: label.optional(),
		/** D179: the start-over door's confirm — its sentence (`{count}` is the locations walked) and its two buttons. */
		restart: z.strictObject({ confirm: label, keepLabel: label, proceedLabel: label }).readonly(),
		/** D179: the start-over door, shown beside `resumeLabel` when a descent is under way. */
		restartLabel: label,
		/** D179: the second-step button when a descent is under way. */
		resumeLabel: label,
		/** The typed status lines; `inProgress` carries `{count}` (D179). */
		status: z.strictObject({ asleep: label, awake: label, inProgress: label }).readonly(),
		theme: ambienceTargetSchema,
		wakeLabel: label,
	})
	.readonly();

/** The clock a pack declares (D153) — the shell's toggle and disclosure read this; the countdown itself is per turn. */
export const clockDeclarationSchema = z
	.strictObject({
		disclosure: label.optional(),
		enabledByDefault: z.boolean(),
	})
	.readonly();

/** What the player is told a game costs (D165): the price, never the account it is paid into — and (D165c) whether the pass covers it. */
export const salePriceSchema = z
	.strictObject({
		currency: z.string().regex(/^[a-z]{3}$/u),
		isInPass: z.boolean().optional(),
		priceCents: z.int().nonnegative(),
	})
	.readonly();

export type SalePrice = z.infer<typeof salePriceSchema>;

/** Everything fixed about a game for the life of a session. */
export const gameDescriptionSchema = z
	.strictObject({
		clock: clockDeclarationSchema.optional(),
		/**
		 * D265: every cue the game can play, by URL — the shell fetches them ahead, so a cue's first play sounds on its
		 * line. Said only when the pack has cues, and optional on the wire: a shell built before it parses strictly.
		 */
		cues: z.array(label).min(1).readonly().optional(),
		gameId: label,
		/** Whether the pack ships any audio at all — the sound control renders only when it does. */
		hasAudio: z.boolean(),
		/**
		 * D242: the pack declares a Record (D241) — the settings panel's "Forget the Record" renders only when it does.
		 * Said ONLY when true, and optional on the wire: a shell built before this field parses the description
		 * strictly, so a game with no Record must answer exactly as it did, and absence means "no Record".
		 */
		hasRecord: z.literal(true).optional(),
		/** The pack's one plate aspect ratio (`width:height`), when it declares images. */
		imageAspectRatio: label.optional(),
		logo: label.optional(),
		play: playModeSchema,
		/** For sale (D165): the price the Buy button shows; absent, the game is free. */
		sale: salePriceSchema.optional(),
		subtitle: label.optional(),
		/**
		 * The face the world speaks in (D203). Optional ON THE WIRE only: a host always says it, but a play Worker
		 * deployed before this field answered without it, and a shell must read that answer as the terminal rather
		 * than refuse the game — absence means `terminal`, exactly as it does in the pack.
		 */
		surface: surfaceSchema.optional(),
		title: label,
		titleScreen: titleScreenViewSchema,
	})
	.readonly();

export type GameDescription = z.infer<typeof gameDescriptionSchema>;
